export type BillingOwnerType = 'candidate' | 'organization';

export interface BillingHistoryPayment {
  id: string;
  payment_type?: string | null;
  status?: string | null;
  total_sar?: number | string | null;
  amount_sar?: number | string | null;
  currency?: string | null;
  created_at?: string | null;
  paid_at?: string | null;
  reference_type?: string | null;
  reference_id?: string | null;
  owner_type?: string | null;
  owner_id?: string | null;
}

export interface BillingHistoryInvoice {
  id: string;
  invoice_number?: string | null;
  payment_transaction_id?: string | null;
  owner_type?: string | null;
  owner_id?: string | null;
  amount_sar?: number | string | null;
  total_sar?: number | string | null;
  currency?: string | null;
  status?: string | null;
  issued_at?: string | null;
  paid_at?: string | null;
  created_at?: string | null;
  vat_amount_sar?: number | string | null;
  vat_rate_percent?: number | string | null;
  line_items?: BillingHistoryLineItem[];
  billing_identity?: BillingHistoryIdentity | null;
  erp_invoice_map?: BillingHistoryErpInvoiceReference[] | null;
  official_artifacts?: BillingHistoryOfficialArtifact[];
  official_email_deliveries?: BillingHistoryOfficialEmailDelivery[];
}

export interface BillingHistoryLineItem {
  line_number: number;
  description: string;
  quantity: number;
  unit_price_sar: number;
  total_sar: number;
}

export interface BillingHistoryIdentity {
  buyer_type: string | null;
  legal_customer_name: string | null;
  vat_number: string | null;
  additional_id_scheme: string | null;
  additional_id_value: string | null;
  commercial_registration_number: string | null;
}

export interface BillingHistoryErpInvoiceReference {
  external_invoice_number: string | null;
}

export interface BillingHistoryOfficialArtifact {
  artifact_type: string;
}

export interface BillingHistoryOfficialEmailDelivery {
  delivery_state: string;
}

export interface BillingHistoryEntry {
  payment: BillingHistoryPayment;
  invoice: BillingHistoryInvoice | null;
  invoiceState: 'available' | 'processing' | 'none';
}

export function sortBillingHistoryPayments<T extends BillingHistoryPayment>(payments: readonly T[]): T[] {
  return [...payments].sort((a, b) => {
    const byDate = Date.parse(b.created_at ?? '') - Date.parse(a.created_at ?? '');
    if (Number.isFinite(byDate) && byDate !== 0) return byDate;
    return String(b.id).localeCompare(String(a.id));
  });
}

export function isCustomerVisibleBillingPayment(payment: BillingHistoryPayment): boolean {
  // Pending is an internal checkout/recovery state. ForkAdmin retains the
  // complete payment-recovery view; customer history shows financial outcomes.
  return String(payment.status ?? '').toLowerCase() !== 'pending';
}

function sortInvoices<T extends BillingHistoryInvoice>(invoices: readonly T[]): T[] {
  return [...invoices].sort((a, b) => {
    const aDate = a.issued_at ?? a.created_at ?? '';
    const bDate = b.issued_at ?? b.created_at ?? '';
    const byDate = Date.parse(bDate) - Date.parse(aDate);
    if (Number.isFinite(byDate) && byDate !== 0) return byDate;
    return String(b.id).localeCompare(String(a.id));
  });
}

export function buildInvoiceByPaymentTransactionId<T extends BillingHistoryInvoice>(
  invoices: readonly T[],
  owner?: { ownerType: BillingOwnerType; ownerId: string | null | undefined },
): Map<string, T> {
  const byPaymentId = new Map<string, T>();
  for (const invoice of sortInvoices(invoices)) {
    if (!invoice.payment_transaction_id) continue;
    if (owner?.ownerId && invoice.owner_type && invoice.owner_type !== owner.ownerType) continue;
    if (owner?.ownerId && invoice.owner_id && invoice.owner_id !== owner.ownerId) continue;
    if (!byPaymentId.has(invoice.payment_transaction_id)) {
      byPaymentId.set(invoice.payment_transaction_id, invoice);
    }
  }
  return byPaymentId;
}

export function expectsInvoiceGeneration(status?: string | null): boolean {
  return status === 'paid' || status === 'succeeded';
}

export function buildBillingHistoryEntries<TPayment extends BillingHistoryPayment, TInvoice extends BillingHistoryInvoice>(
  payments: readonly TPayment[],
  invoices: readonly TInvoice[],
  options?: {
    limit?: number;
    ownerType?: BillingOwnerType;
    ownerId?: string | null;
  },
): Array<BillingHistoryEntry & { payment: TPayment; invoice: TInvoice | null }> {
  const invoiceByPaymentId = buildInvoiceByPaymentTransactionId(invoices, {
    ownerType: options?.ownerType ?? 'organization',
    ownerId: options?.ownerId,
  });

  return sortBillingHistoryPayments(payments.filter(isCustomerVisibleBillingPayment))
    .slice(0, options?.limit)
    .map((payment) => {
      const invoice = invoiceByPaymentId.get(payment.id) ?? null;
      return {
        payment,
        invoice,
        invoiceState: invoice ? 'available' : expectsInvoiceGeneration(payment.status) ? 'processing' : 'none',
      };
    });
}
